export type ParsedPriceBid =
  | {
      prices: number[]
      volumes: number[][]
    }
  | undefined

/**
 * Will parse the text from the clipboard to an array of PTU values
 * Empty lines will be treated as 0
 * It treats small numbers (< 1000) as MW and converts to Watts
 * Bigger numbers are treated as Watts
 * The returned values, in watts, are truncated to be multiples of 10kW (or 0.01MW)
 */
export const parseVolumeBid = (text: string) => {
  const lines = text.split('\n')

  const newPtuValues = lines
    .map((line) => {
      if (line === '') return 0

      return Number.parseFloat(line)
    })
    .filter((value) => !Number.isNaN(value))

  return newPtuValues.map((value) => {
    if (value < 1000) {
      //values in MW, we need to truncate decimal places and convert to Watts
      return 1_000_000 * (Math.trunc(value * 100) / 100)
    } else {
      //value in Watts, remove the least significant digits so it shows as MW in the UI with max 2 decimals
      return Math.trunc(value / 10_000) * 10_000
    }
  })
}

export const parsePriceBid = (text: string): ParsedPriceBid => {
  if (text.length === 0) {
    return
  }

  const lines = text
    .split('\n')
    // eslint-disable-next-line sonarjs/slow-regex
    .map((line) => line.replace(/\t+$/, ''))
    .filter((line) => line !== '')
    .filter((line) => !line.startsWith('\t'))

  const parsedPrices = parsePrices(lines)

  return {
    prices: parsedPrices,
    volumes: parseVolumes(lines, parsedPrices),
  }
}

const parsePrices = (lines: string[]): number[] => {
  try {
    const prices = parseLine(lines[0], true)
    validatePrices(prices)
    return prices
  } catch (e: any) {
    throw new Error(`Price error: ${e.message}`)
  }
}

const parseVolumes = (lines: string[], prices: number[]): number[][] =>
  lines.slice(1).map((line, index) => {
    let volumesPerPtu: number[] | void[] = []

    try {
      volumesPerPtu = parseLine(line, false)
    } catch (e: any) {
      throw new Error(`Volume error for PTU ${index}: ${e.message}`)
    }

    if (volumesPerPtu.length !== prices.length) {
      throw new Error(`Volume error for PTU ${index}: Missing volumes`)
    }

    return volumesPerPtu
  })

const parseLine = (line: string, acceptsNegative: boolean = true) => {
  const values = line.split('\t').slice(1)
  const parsedValues: number[] = []
  const missingValueCells: any[] = []
  const negativeValueCells: any[] = []

  for (const [index, value] of values.entries()) {
    const parsedValue = Number.parseFloat(value)

    // we need to round with max 2 decimal places
    parsedValues.push(Math.round(parsedValue * 100) / 100)
    if (Number.isNaN(parsedValue)) {
      missingValueCells.push(index + 1)
    }

    if (!acceptsNegative && parsedValue < 0) {
      negativeValueCells.push(index + 1)
    }
  }

  if (missingValueCells.length > 0) {
    throw new Error(`Missing or invalid value in column(s) ${missingValueCells.join(', ')}`)
  }

  if (!acceptsNegative && negativeValueCells.length > 0) {
    throw new Error(`Negative volume value in column(s) ${negativeValueCells.join(', ')}`)
  }

  return parsedValues
}

const validatePrices = (prices: number[]) => {
  const uniquePrices = new Set(prices)
  if (uniquePrices.size !== prices.length) {
    const duplicates = prices.filter((item, index) => prices.indexOf(item) !== index)
    throw new Error(`Duplicate prices found: ${duplicates.join(', ')}`)
  }
}
